import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import {
    ProductCategorySearchBuilder,
    ProductCategorySearchResponse,
    ProductSearchBuilder,
    ProductSearchResponse,
    SearchCollectionBuilder,
    Searcher,
    SearchTermPredictionBuilder,
    SearchTermPredictionResponse
} from '@relewise/client';
import { RelewiseCategoryResult, RelewiseProductResult, ProductData } from '../types/relewiseData';
import { CategoryProductsResponse, MappedProduct, SearchSuggestions } from '../types/mapped';
import {
    NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP,
    DEFAULT_PAGE_SIZE,
    NUMBER_OF_VARIANTS_PER_PRODUCT_IN_AUTOSUGGEST,
    DISTINCT_COLOR_VARIANT_COUNT,
    RELEWISE_DISPLAYED_AT_LOCATION,
    ALL_VARIANTS_EXPLODE_COUNT,
    CATCH_ALL_SEARCH_TERM
} from '../const';
import { FilterOptions } from '../../../utils/productListFilters';
import { ProductListSortOption } from '../../../utils/productListSort';
import { RelewiseMappingService } from './MappingService';
import { BaseRelewiseService, InitializedRelewiseService } from './BaseRelewiseService/BaseRelewiseService';
import type { Gender } from '../../../shared/services/gender-cookie-service/gender-cookie-service.types';

export interface AutosuggestSearchParams {
    query: string;
    gender?: Gender;
    shouldFetchProducts: boolean;
    shouldFetchCategories: boolean;
    shouldFetchSearchTermPredictions: boolean;
    numberOfProductResults: number;
    numberOfCategoryResults: number;
    numberOfKeywordResults: number;
}

interface InitializedService extends InitializedRelewiseService {
    searcher: Searcher;
    relewiseMappingService: RelewiseMappingService;
}

export class RelewiseSearchService extends BaseRelewiseService<InitializedService> {
    protected searcher: Searcher | undefined;
    protected relewiseMappingService: RelewiseMappingService | undefined;

    constructor(request: IRequestContext, telemetry: ITelemetry) {
        super(request, telemetry, 'Search Service');

        if (!this.isEnabled()) {
            return;
        }

        this.relewiseMappingService = new RelewiseMappingService(request, telemetry);
        this.searcher = new Searcher(this.relewiseDatasetId, this.relewiseApiKey, {
            serverUrl: this.relewiseUrl
        });
    }

    public async autosuggestedSearch(
        {
            query,
            gender,
            shouldFetchProducts,
            shouldFetchCategories,
            shouldFetchSearchTermPredictions,
            numberOfProductResults,
            numberOfCategoryResults,
            numberOfKeywordResults
        }: AutosuggestSearchParams,
        abortSignal?: AbortSignal
    ): Promise<SearchSuggestions | undefined> {
        const shouldFetchSomething = shouldFetchProducts || shouldFetchCategories || shouldFetchSearchTermPredictions;
        if (!this.isEnabled() || !query || !shouldFetchSomething) {
            return;
        }

        try {
            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.HEADER_SEARCH);

            const batchedRequestBuilder = new SearchCollectionBuilder();

            if (shouldFetchProducts) {
                const productBuilder = new ProductSearchBuilder(settings)
                    .pagination(p => p.setPageSize(numberOfProductResults))
                    .setVariantRequestSettings(variantSettings => {
                        variantSettings.setMaxVariantsPerProduct(NUMBER_OF_VARIANTS_PER_PRODUCT_IN_AUTOSUGGEST);
                    })
                    .searchConstraints(searchConstraints => {
                        searchConstraints.setResultMustHaveVariantConstraint({
                            exceptWhenProductHasNoVariants: false
                        });
                    })
                    .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                    .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                    .filters(f => {
                        this.addProductEnvironmentFilter(f);
                        this.addProductAndVariantStockFilters(f);
                        if (gender) {
                            f.addProductDataFilter('Gender', builder =>
                                builder.addEqualsCondition({ value: gender, type: 'String', isCollection: false })
                            );
                        }
                        f.addVariantDataFilter('UniqueColorId', b => b.addDistinctCondition(1), true, true);
                    });

                this.setSearchTermOrMultipleIdsFilter(productBuilder, query);
                batchedRequestBuilder.addRequest(productBuilder.build());
            }

            if (shouldFetchCategories) {
                const categoryBuilder = new ProductCategorySearchBuilder(settings)
                    .setTerm(query)
                    .pagination(p => p.setPageSize(numberOfCategoryResults))
                    .setSelectedCategoryProperties({
                        displayName: true,
                        dataKeys: ['Url']
                    })
                    .filters(f => {
                        this.addCategoryEnvironmentFilter(f);
                        if (gender) {
                            f.addProductCategoryDataFilter('Gender', builder =>
                                builder.addEqualsCondition({ value: gender, type: 'String', isCollection: false })
                            );
                        }
                        f.addProductCategoryDataFilter(
                            'LegalEntity',
                            b => b.addEqualsCondition({ value: this.legalEntityId, type: 'String', isCollection: false }),
                            true,
                            true
                        );
                    });
                batchedRequestBuilder.addRequest(categoryBuilder.build());
            }

            if (shouldFetchSearchTermPredictions) {
                const searchTermPredictionBuilder = new SearchTermPredictionBuilder(settings).setTerm(query).take(numberOfKeywordResults);
                if (gender) {
                    searchTermPredictionBuilder.addEntityType('Product', 'ProductCategory').filters(filters => {
                        filters.addProductDataFilter('Gender', builder =>
                            builder.addEqualsCondition({ value: gender, type: 'String', isCollection: false })
                        );
                        filters.addProductCategoryDataFilter('Gender', builder =>
                            builder.addEqualsCondition({ value: gender, type: 'String', isCollection: false })
                        );
                    });
                }
                batchedRequestBuilder.addRequest(searchTermPredictionBuilder.build());
            }

            const batchResponse = await this.searcher.batch(batchedRequestBuilder.build(), { abortSignal });

            let searchTermPredictionResponse: SearchTermPredictionResponse | undefined;
            let categoryResponse: ProductCategorySearchResponse | undefined;
            let productResponse: ProductSearchResponse | undefined;
            if (shouldFetchSearchTermPredictions) {
                searchTermPredictionResponse = batchResponse?.responses?.pop() as SearchTermPredictionResponse;
            }
            if (shouldFetchCategories) {
                categoryResponse = batchResponse?.responses?.pop() as ProductCategorySearchResponse;
            }
            if (shouldFetchProducts) {
                productResponse = batchResponse?.responses?.pop() as ProductSearchResponse;
            }

            const products = (productResponse?.results ?? undefined) as RelewiseProductResult[] | undefined;
            const searchTermPredictions = searchTermPredictionResponse?.predictions ?? undefined;
            const categories = (categoryResponse?.results ?? undefined) as RelewiseCategoryResult[] | undefined;

            const mappedResult = this.relewiseMappingService.mapAutosuggestedSearchResult({
                products,
                categories,
                searchTermPredictions
            });

            return mappedResult;
        } catch (err) {
            if (!abortSignal?.aborted) {
                this.logError(`autosuggestedSearch request failed: ${err instanceof Error ? err.message : String(err)}`);
            }
            return;
        }
    }

    public async getSearchProducts(
        query: string,
        itemsPerPage: number = DEFAULT_PAGE_SIZE,
        page: number = 1,
        filterOptions?: FilterOptions,
        sort: ProductListSortOption = 'recommended',
        variantsPerProduct: number = NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP
    ): Promise<CategoryProductsResponse | undefined> {
        if (!query || !this.isEnabled()) {
            return;
        }

        try {
            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.SEARCH_PLP);

            const builder = new ProductSearchBuilder(settings)
                .pagination(p => p.setPageSize(itemsPerPage).setPage(page))
                .setVariantRequestSettings(variantSettings => {
                    variantSettings.setMaxVariantsPerProduct(variantsPerProduct);
                })
                .searchConstraints(searchConstraints => {
                    searchConstraints.setResultMustHaveVariantConstraint({
                        exceptWhenProductHasNoVariants: false
                    });
                })
                .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                .facets(f => {
                    f.addSalesPriceRangeFacet('Product', filterOptions?.priceRange?.min, filterOptions?.priceRange?.max);
                    f.addCategoryFacet('ImmediateParent', filterOptions?.categoryIds);
                    f.addProductDataStringValueFacet('Size', 'Variant', filterOptions?.sizes);
                    f.addProductDataStringValueFacet('ColorRefinerGroup', 'Variant', filterOptions?.colors);
                })
                .filters(f => {
                    this.addProductEnvironmentFilter(f);
                    this.addProductAndVariantStockFilters(f);
                    if (filterOptions?.gender) {
                        f.addProductDataFilter('Gender', builder =>
                            builder.addEqualsCondition({ value: filterOptions.gender, type: 'String', isCollection: false })
                        );
                    }
                    f.addVariantDataFilter('UniqueColorId', b => b.addDistinctCondition(DISTINCT_COLOR_VARIANT_COUNT), true, true);
                });

            this.setSearchTermOrMultipleIdsFilter(builder, query);

            if (sort === 'price-asc') {
                builder.sorting(s =>
                    s.sortByProductVariantAttribute('SalesPrice', 'Ascending', thenBy => thenBy.sortByProductRelevance('Descending'))
                );
            } else if (sort === 'price-desc') {
                builder.sorting(s =>
                    s.sortByProductVariantAttribute('SalesPrice', 'Descending', thenBy => thenBy.sortByProductRelevance('Descending'))
                );
            }

            const response = await this.searcher.searchProducts(builder.build());

            if (!response?.results) {
                return {
                    productList: [],
                    totalItems: 0,
                    currentPage: page,
                    itemsPerPage,
                    totalPages: 0
                };
            }

            const productList = response.results
                .filter(product => product.variant !== null)
                .map(product => ({
                    variant: this.relewiseMappingService?.mapRelewiseProductResult(product as RelewiseProductResult),
                    colors: this.relewiseMappingService?.mapProductColors(product.data as ProductData) || []
                }));

            const totalItems = response.hits ?? 0;
            const totalPages = itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 0;
            const redirects = (response.redirects ?? [])
                .map(redirect => ({ url: redirect.destination }))
                .filter((urlObj): urlObj is { url: string } => Boolean(urlObj.url));
            return {
                productList,
                totalItems,
                currentPage: page,
                itemsPerPage,
                totalPages,
                redirects
            };
        } catch (err) {
            this.logError(`getSearchProducts request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }

    public async findProductsByItemIdAndVariantColor(uniqueColorIds: string[]): Promise<MappedProduct[] | undefined> {
        const requestedColorIds = Array.from(new Set(uniqueColorIds.map(uniqueColorId => uniqueColorId.trim()).filter(Boolean)));
        if (!requestedColorIds.length) {
            return [];
        }
        if (!this.isEnabled()) {
            return;
        }

        try {
            const mappingService = this.relewiseMappingService;
            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.LANDING_PAGE);
            const builder = new ProductSearchBuilder(settings)
                .pagination(pagination => pagination.setPageSize(requestedColorIds.length))
                .searchConstraints(constraints => constraints.setResultMustHaveVariantConstraint({ exceptWhenProductHasNoVariants: false }))
                .setVariantRequestSettings(variants => variants.setMaxVariantsPerProduct(requestedColorIds.length))
                .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                .filters(filters => {
                    this.addProductEnvironmentFilter(filters);
                    filters.or(colorFilters => {
                        requestedColorIds.forEach(uniqueColorId => {
                            colorFilters.addVariantDataFilter(
                                'UniqueColorId',
                                conditions => conditions.addEqualsCondition({ value: uniqueColorId, type: 'String', isCollection: false }),
                                true,
                                true
                            );
                        });
                    });
                    filters.addVariantDataFilter(
                        `ColorHasVisibleVariant_${this.legalEntityId}`,
                        conditions => conditions.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                        true,
                        true
                    );
                    filters.addVariantDataFilter(
                        'UniqueColorId',
                        conditions => conditions.addDistinctCondition(DISTINCT_COLOR_VARIANT_COUNT),
                        true,
                        true
                    );
                });

            const response = await this.searcher.searchProducts(builder.build());
            if (!response) {
                throw new Error('No response from Relewise for products by item ID and variant color.');
            }

            const result = ((response.results ?? []) as RelewiseProductResult[]).filter(
                product => !!product.variant?.data?.RelativeUrl?.value && Number.isFinite(product.variant.salesPrice)
            );
            return mappingService
                .mapRelewiseProductResultList(result)
                .filter(product => !!product.productItemId && !!product.productRecordId && !!product.variantRecordId);
        } catch (error) {
            this.logError(`findProductsByItemIdAndVariantColor request failed: ${error instanceof Error ? error.message : String(error)}`);
            return;
        }
    }

    // Returns undefined when the request fails, [] when it succeeds but no variants are visible
    public async findVariantsByIds(variantRecordIds: number[]): Promise<MappedProduct[] | undefined> {
        if (!variantRecordIds.length || !this.isEnabled()) {
            return;
        }

        try {
            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.SEARCH_PLP);
            const numberOfItems = variantRecordIds.length;

            const builder = new ProductSearchBuilder(settings)
                .pagination(p => p.setPageSize(numberOfItems))
                .searchConstraints(searchConstraints => {
                    searchConstraints.setResultMustHaveVariantConstraint({
                        exceptWhenProductHasNoVariants: false
                    });
                })
                .setVariantRequestSettings(variantSettings => {
                    variantSettings.setMaxVariantsPerProduct(ALL_VARIANTS_EXPLODE_COUNT);
                })
                .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                .filters(f => {
                    this.addProductEnvironmentFilter(f);
                    f.or(orFilter => {
                        variantRecordIds.forEach(variantRecordId => {
                            orFilter.addVariantDataFilter(
                                'RecordId',
                                b => {
                                    b.addEqualsCondition({ value: variantRecordId, type: 'Double', isCollection: false });
                                },
                                true,
                                true
                            );
                        });
                    });
                    f.addVariantDataFilter(
                        `ColorHasVisibleVariant_${this.legalEntityId}`,
                        b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                        true,
                        true
                    );
                });

            const response = await this.searcher.searchProducts(builder.build());
            if (!response) {
                throw new Error(`No response from Relewise for variant record ids: ${variantRecordIds.join(', ')}`);
            }

            const result = (response.results ?? []) as RelewiseProductResult[];

            const mappedProducts = this.relewiseMappingService.mapRelewiseProductResultList(result);

            // Sort based on input ids, since Relewise does not guarantee the order of results
            const sortedProducts = mappedProducts.sort((a, b) => {
                const aIndex = variantRecordIds.indexOf(a.variantRecordId ?? -1);
                const bIndex = variantRecordIds.indexOf(b.variantRecordId ?? -1);
                return aIndex - bIndex;
            });

            return sortedProducts;
        } catch (err) {
            this.logError(`findVariantsByIds request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }

    private setSearchTermOrMultipleIdsFilter(builder: ProductSearchBuilder, query: string): void {
        if (!this.isEnabled()) {
            return;
        }

        // Do not apply search term or filter when using catch-all search term
        if (query === CATCH_ALL_SEARCH_TERM) {
            return;
        }

        const ids = query.split('/').filter(id => id.length > 0);
        const isMultipleIds = ids.length > 1 && ids.every(id => !isNaN(Number(id)));

        if (isMultipleIds) {
            const multipleIdsQuery = ids.map(id => this.relewiseMappingService!.prefixId(id));
            builder.filters(f => {
                f.addProductIdFilter(multipleIdsQuery);
            });
        } else {
            builder.setTerm(query);
        }
    }
}
